<script lang="ts">
	import { getTranslate } from "@utils/getTranslate.js";
	import plus from "@core/assets/icons/plus.svg?raw";
	import type { Country } from "@core/schema/Country.js";
	import { Currency } from "@core/schema/Currency.js";
	import { EconomicSubjectVatType } from "@core/schema/EconomicSubjectVatType.js";
	import type { ProductOfVariantForCustomer, ProductVariantForCustomer } from "@core/schema/Product.js";
	import { ProductVariantType } from "@core/schema/ProductVariantType.js";
	import type { SubcategoryForCustomer } from "@core/schema/Subcategory.js";
	import { getProductDetailPath } from "@core/schema/paths/getProductDetailPath.js";
	import { getPriceForCustomerOfProductVariant } from "@core/schema/utils/getPriceForCustomerOfProductVariant.js";
	import { getProductHeadingWithAttributes } from "@core/schema/utils/getProductHeadingWithAttributes.js";
	import { getProductOfVariant } from "@core/schema/utils/getProductOfVariant.js";
	import { getProductPrimaryMedia } from "@core/schema/utils/getProductPrimaryMedia.js";
	import { getProductStockAmount } from "@core/schema/utils/getProductStockAmount.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { Cart } from "../cart/Cart.svelte.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import PreloadedPrice from "./PreloadedPrice.svelte";
	import translations from "./ProductB2bModal.translations.json" with { type: "json" };
	import type { ProductForCard } from "./ProductCard.js";
	import ProductCardCartOptions from "./ProductCardCartOptions.svelte";
	import ProductCardMedia from "./ProductCardMedia.svelte";
	import ProductCardMediaFallback from "./ProductCardMediaFallback.svelte";
	import ProductTags from "./ProductTags.svelte";
	import Button from "./common/Button.svelte";
	import { ButtonSize } from "./common/ButtonSize.js";
	import { ButtonVariant } from "./common/ButtonVariant.js";
	import CkEditorContent from "./common/CkEditorContent.svelte";
	import Formatted from "./common/Formatted.svelte";
	import Heading from "./common/Heading.svelte";
	import Icon from "./common/Icon.svelte";
	import Link from "./common/Link.svelte";
	import SideModal from "./common/SideModal.svelte";
	import { getTagsForProductCard } from "./getTagsForProductCard.js";

	const { tenant } = getGlobalTemplateProps();
	let {
		show = $bindable(),
		/* eslint-disable prefer-const */
		product,
		subcategory,
		productPrimaryVariant,
		/* eslint-enable prefer-const */
	}: {
		product: ProductForCard;
		subcategory: SubcategoryForCustomer;
		productPrimaryVariant: ProductOfVariantForCustomer;
		show: boolean;
	} = $props();

	const translate = getTranslate(translations);
	const mediaSrc = $derived(getProductPrimaryMedia(productPrimaryVariant, product.attributes));
	const heading = $derived(getProductHeadingWithAttributes(tenant.locale, product));

	const [defaultVariant] = $derived(product.variants) as [ProductVariantForCustomer];
	const link = $derived(getProductDetailPath(tenant.locale, product, subcategory, defaultVariant, product.attributes));
</script>

<SideModal bind:show {heading} fullContentWidth hideControls>
	<button
		type="button"
		class="border-fcrgray-300 md:border size-6 md:size-15 lg:size-18 flex items-center justify-center rounded-full absolute bg-white right-4 top-4.5 md:top-6 md:right-6 z-10 cursor-pointer"
		onclick={(): void => {
			show = false;
		}}
		aria-label={translate("close")}
	>
		<Icon icon={plus} class="size-6 rotate-45" />
	</button>

	<div>
		<div
			class="flex justify-between items-center px-4 py-4.5 border-b border-fcrlightgray-300 md:border-none md:pt-6 md:pr-6 md:pl-12 md:hidden"
		>
			<Heading
				level={2}
				text={heading}
				class="text-fcrdarkgray font-medium font-antipol text-5/tighter md:text-10/tighter "
			/>
		</div>

		<div class="bg-[#F6F6F6] w-full h-52 md:h-102 flex overflow-hidden flex-col p-4 justify-between relative">
			{#if productPrimaryVariant}
				<ProductTags tags={getTagsForProductCard({ product, locale: tenant.locale, subcategory })} />
				{#if mediaSrc}
					<ProductCardMedia {mediaSrc} class="!object-contain" />
				{:else}
					<ProductCardMediaFallback />
				{/if}
			{/if}
		</div>
		<div class="px-10 pt-8 pb-4">
			<a href={link} class="text-fcrdarkgray hover:text-fcrbrown underline">
				<Heading level={2} text={heading} class="font-medium font-antipol hidden md:block md:text-10/tighter " />
			</a>
		</div>
		<div class="grid grid-cols-4">
			{#each product.variants as variant (variant)}
				{@const productOfVariant = getProductOfVariant(product, variant.id)}
				<div
					class="grid grid-cols-subgrid col-span-full border-t border-fcrlightgray last-of-type:border-b py-4 px-12 items-center"
				>
					<div class="text-fcrdarkgray font-medium text-4 font-antipol">
						{#if product.variantType !== ProductVariantType.None}
							<Formatted text={variant.label[tenant.locale]} />
						{/if}
					</div>
					<div class="text-fcrdarkgray font-medium text-4 font-antipol">
						<PreloadedPrice class="h-6">
							{#snippet children(currency: Currency, destinationCountry: Country | "outsideEu")}
								{@const productPrice = getPriceForCustomerOfProductVariant(
									productOfVariant,
									currency,
									destinationCountry,
									EconomicSubjectVatType.NotVatSubject,
								).withTax}
								{#if currency === Currency.CZK}
									<Formatted text={formatPrice(Math.ceil(productPrice), currency, tenant.locale, false)} />
								{:else}
									<Formatted text={formatPrice(productPrice, currency, tenant.locale, true)} />
								{/if}
							{/snippet}
						</PreloadedPrice>
					</div>
					<div class="w-42.5 flex items-center ml-auto col-span-2 justify-end">
						<ProductCardCartOptions
							showToasts={false}
							product={productOfVariant}
							attributes={product.attributes}
							max={getProductStockAmount(product.stock, variant, product.attributes)}
						/>
					</div>
				</div>
			{/each}
		</div>

		<div class="px-10 py-6">
			<CkEditorContent html={product.description[tenant.locale]} class="text-sm/normal sm:text-base text-fcrgray" />
		</div>

		<div class="p-6 bg-white inset-x-0 grid grid-cols-1 md:grid-cols-2 gap-4">
			<Button
				variant={ButtonVariant.Dark}
				size={ButtonSize.Large}
				class="w-full"
				onclick={(): void => {
					show = false;
					Cart.showModal = true;
				}}
			>
				{translate("continueToCart")}
			</Button>
			<Link href={link} variant={ButtonVariant.Outlined} size={ButtonSize.Large}>
				<Formatted text={translate("viewProduct")} />
			</Link>
		</div>
	</div>
</SideModal>
